/**
 * @file api-site-seo.ts
 * @description Клиент общих SEO-настроек магазина «Сайт → SEO → Основное»
 * и «Проверки сайта» (phase19.16, партия Р3в, v0.19.64; SB145, SB132,
 * SEO1, SEO16; контракт r3v-api-contract.md §5, §6).
 *
 * Настройки действуют сразу после сохранения, без черновика вида сайта:
 * запись шлёт `expectedVersion` (409 `site_seo_version_conflict` — изменено
 * в другом окне) и только изменённые поля; ответ записи — `{ settings,
 * warnings }`. Закрытие от поисковиков — `allowIndexing: false` вместе с
 * `confirm: "закрыть"`. Журнал «было/стало» — последние 10 значений поля,
 * «Вернуть прежнее» — `revert` строки журнала.
 *
 * «SEO-техника» v0.19.65 (SEO8, SB146; db-design-seo-tech §4.1): в ответе —
 * карточка IndexNow (`indexNow`: включено, почему не отправляем, ключи
 * адресов, последняя отправка) и показ крошек на живом сайте
 * (`navigation.breadcrumbs`, для карточки «Разметка для поиска»); поле
 * `indexNowEnabled` пишется тем же `PATCH` с журналом «было/стало».
 *
 * `apiFetch` разворачивает `{ data }`; ошибка приходит `ApiError` с `code` и
 * `details` — текст для человека подбирает `site-seo/seo-errors.ts`.
 */

import type { SiteSeoIssueCode, SiteSeoIssueLevel } from "@chaiberi/shared";

import { apiFetch } from "./api-client";
import type { ShopSeoTechFields } from "./site-seo/seo-types";

const BASE = "/admin/site/seo";

// ─── DTO (контракт §5) ───────────────────────────────────────────────────

/** Поле настроек: в ответе, теле `PATCH`, журнале и отметках «изменено». */
export type ShopSeoFieldKey =
  | "defaultTitle"
  | "titleSuffix"
  | "description"
  | "ogImageMediaId"
  | "appShortName"
  | "allowIndexing"
  | "yandexVerification"
  | "googleVerification"
  | "indexNowEnabled";

/** Картинка для ссылок с превью. */
export interface ShopSeoImage {
  id: string;
  url: string;
  alt: string | null;
  width: number | null;
  height: number | null;
}

/** Значение поля в журнале и в плашке неопубликованных правок. */
export type ShopSeoValue = string | boolean | string[] | null;

/** Неопубликованные SEO-правки черновика вида сайта, найденные при переносе (§2а). */
export interface ShopSeoPending {
  detectedAt: string;
  fields: { field: ShopSeoFieldKey; label: string; value: ShopSeoValue }[];
}

/** `GET /admin/site/seo`; v0.19.65 — с IndexNow и крошками живого сайта (`seo-types.ts`). */
export interface ShopSeoSettingsDto extends ShopSeoTechFields {
  defaultTitle: string | null;
  titleSuffix: string | null;
  description: string | null;
  ogImageMediaId: string | null;
  ogImage: ShopSeoImage | null;
  appShortName: string | null;
  allowIndexing: boolean;
  verification: { yandex: string[]; google: string[] };
  /** Оптимистическая блокировка: прислать обратно в `expectedVersion`. */
  version: number;
  /** `null` — строки ещё нет (умолчания платформы). */
  updatedAt: string | null;
  pending: ShopSeoPending | null;
  /** «Изменено когда и кем» по полю — последняя запись журнала. */
  lastChanges: Partial<Record<ShopSeoFieldKey, { at: string; by: string | null }>>;
}

/** Тело `PATCH /admin/site/seo`: только изменённые поля. */
export interface ShopSeoPatch {
  expectedVersion: number;
  defaultTitle?: string | null;
  titleSuffix?: string | null;
  description?: string | null;
  ogImageMediaId?: string | null;
  appShortName?: string | null;
  allowIndexing?: boolean;
  /** «закрыть» — обязательно при `allowIndexing: false` (сервер проверяет). */
  confirm?: string;
  /** Код или мета-тег целиком; пустые строки сервер пропускает. */
  yandexVerification?: string[];
  googleVerification?: string[];
  /** v0.19.65 (SEO8): «Сообщать поисковикам об изменениях». */
  indexNowEnabled?: boolean;
  /** SB125: сохранить, несмотря на стоп-лист (галочка владельца). */
  acknowledgeChecks?: boolean;
}

/** Предупреждения записи — сохранено, но стоит сказать. */
export type ShopSeoWarning =
  | { code: "site_seo_search_update_delay"; fields: ShopSeoFieldKey[] }
  | { code: "site_seo_verification_format"; engine: "yandex" | "google"; code_value: string }
  | { code: "site_seo_text_acknowledged"; checks: string[] };

/** Ответ записи: настройки и предупреждения. */
export interface ShopSeoWriteResult {
  settings: ShopSeoSettingsDto;
  warnings: ShopSeoWarning[];
}

export type ShopSeoChangeSource = "EDIT" | "REVERT" | "LEGACY_DRAFT";

/** Строка журнала «было/стало». */
export interface ShopSeoHistoryItem {
  id: string;
  field: ShopSeoFieldKey;
  oldValue: ShopSeoValue;
  newValue: ShopSeoValue;
  source: ShopSeoChangeSource;
  actorName: string | null;
  createdAt: string;
  /** Прежнее значение отличается от текущего — возвращать есть что. */
  canRevert: boolean;
}

// ─── «Проверка сайта» (контракт §6) ──────────────────────────────────────

export type SiteSeoSubjectKind = "site" | "page" | "product" | "category" | "article" | "blog-category" | "menu" | "redirects";

/** Одно замечание. */
export interface SiteSeoIssueDto {
  code: SiteSeoIssueCode;
  level: SiteSeoIssueLevel;
  /** `menuLocation` — у пункта меню: где он стоит (контракт §6). */
  subject: { kind: SiteSeoSubjectKind; id: string | null; title: string; menuLocation?: "HEADER" | "FOOTER" | null };
  /** Куда вести «Исправить →» — путь кабинета магазина без `/shop/<slug>`. */
  fixUrl: string;
  details?: Record<string, unknown>;
}

/** `GET /admin/site/seo/audit`. */
export interface SiteSeoAuditDto {
  checkedAt: string;
  cached: boolean;
  banner: "seo_site_noindex" | null;
  counts: { warning: number; info: number };
  issues: SiteSeoIssueDto[];
}

// ─── Клиент ──────────────────────────────────────────────────────────────

/** Тело возврата и плашки: версия, слово «закрыть» и галочка стоп-листа (SB125). */
export interface ShopSeoVersionBody {
  expectedVersion: number;
  confirm?: string;
  acknowledgeChecks?: boolean;
}

/** Необязательные поля записи «Вернуть прежнее» и «Применить». */
export type ShopSeoWriteOptions = Omit<ShopSeoVersionBody, "expectedVersion">;

export const shopSeo = {
  /** Настройки, версия, отметки «изменено» и неопубликованные правки черновика. */
  get: (): Promise<ShopSeoSettingsDto> => apiFetch<ShopSeoSettingsDto>(BASE),

  /** Сохранить изменённые поля — действуют сразу (сброс site-config). */
  save: (patch: ShopSeoPatch): Promise<ShopSeoWriteResult> => apiFetch<ShopSeoWriteResult>(BASE, { method: "PATCH", json: patch }),

  /** Последние 10 значений поля за 12 месяцев, новые сверху. */
  history: async (field: ShopSeoFieldKey): Promise<ShopSeoHistoryItem[]> =>
    (await apiFetch<{ items: ShopSeoHistoryItem[] }>(`${BASE}/history?field=${encodeURIComponent(field)}`)).items,

  /** «Вернуть прежнее»: полю — значение «было» строки журнала. */
  revert: (historyId: string, body: ShopSeoVersionBody): Promise<ShopSeoWriteResult> =>
    apiFetch<ShopSeoWriteResult>(`${BASE}/history/${encodeURIComponent(historyId)}/revert`, { method: "POST", json: body }),

  /** «Применить» неопубликованные SEO-правки черновика вида сайта. */
  applyPending: (body: ShopSeoVersionBody): Promise<ShopSeoWriteResult> =>
    apiFetch<ShopSeoWriteResult>(`${BASE}/pending/apply`, { method: "POST", json: body }),

  /** «Отбросить» их — остаются действующие настройки. */
  discardPending: (body: ShopSeoVersionBody): Promise<ShopSeoWriteResult> =>
    apiFetch<ShopSeoWriteResult>(`${BASE}/pending/discard`, { method: "POST", json: body }),

  /** «Проверка сайта»: из памяти API (10 мин) или заново (`refresh`). */
  audit: (refresh = false): Promise<SiteSeoAuditDto> => apiFetch<SiteSeoAuditDto>(`${BASE}/audit${refresh ? "?refresh=1" : ""}`),

  /** SEO-проверки одной страницы по черновику (окно публикации, SB127). */
  pageCheck: async (pageId: string): Promise<SiteSeoIssueDto[]> =>
    (await apiFetch<{ issues: SiteSeoIssueDto[] }>(`/admin/site/pages/${encodeURIComponent(pageId)}/seo-check`)).issues,
};
